import React, { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

export default function VansList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [vans, setVans] = useState([])
  const [loading, setLoading] = useState(true)
  // Read filter from URL params, default to 'all'
  const [filter, setFilter] = useState(searchParams.get('filter') || 'all')
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState('date_sold')
  const [sortDirection, setSortDirection] = useState('desc')

  useEffect(() => {
    fetchVans()
  }, [filter, search])

  // Helper function to update filter and URL params
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter)
    // Update URL params
    if (newFilter === 'all') {
      searchParams.delete('filter')
    } else {
      searchParams.set('filter', newFilter)
    }
    setSearchParams(searchParams)
  }

  const fetchVans = () => {
    setLoading(true)
    let url = '/api/vans'
    const params = new URLSearchParams()

    if (filter === 'inventory') params.append('sold', 'false')
    if (filter === 'sold') params.append('sold', 'true')
    if (search) params.append('search', search)

    if (params.toString()) url += '?' + params.toString()

    fetch(url)
      .then(response => response.json())
      .then(data => {
        setVans(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching vans:', err)
        setLoading(false)
      })
  }

  const deleteVan = (id) => {
    if (!confirm('Are you sure you want to delete this van?')) return

    fetch(`/api/vans/${id}`, {
      method: 'DELETE'
    })
      .then(() => {
        setVans(vans.filter(van => van.id !== id))
      })
      .catch(err => console.error('Error deleting van:', err))
  }

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    // Parse date as local date to avoid timezone issues
    // dateString format: "YYYY-MM-DD"
    const [year, month, day] = dateString.split('-')
    const date = new Date(year, month - 1, day) // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const calculateTotalSalePrice = () => {
    return sortedVans.reduce((total, van) => {
      return total + (parseFloat(van.retail_sale_price) || 0)
    }, 0)
  }

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New field, default to ascending
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedVans = [...vans].sort((a, b) => {
    let aVal = a[sortField]
    let bVal = b[sortField]

    // Handle null/undefined values
    if (aVal === null || aVal === undefined) aVal = ''
    if (bVal === null || bVal === undefined) bVal = ''

    // Handle date fields
    if (sortField === 'date_acquired' || sortField === 'date_sold') {
      aVal = aVal ? new Date(aVal).getTime() : 0
      bVal = bVal ? new Date(bVal).getTime() : 0
    }

    // Handle numeric fields
    if (sortField === 'retail_sale_price') {
      aVal = parseFloat(aVal) || 0
      bVal = parseFloat(bVal) || 0
    }

    // String comparison for text fields
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = bVal.toLowerCase()
    }

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
    }
  })

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return (
        <span className="ml-1 text-gray-400">
          <svg className="inline w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 10l5-5 5 5H5z"/>
          </svg>
        </span>
      )
    }
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? (
          <svg className="inline w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 10l5-5 5 5H5z"/>
          </svg>
        ) : (
          <svg className="inline w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M15 10l-5 5-5-5h10z"/>
          </svg>
        )}
      </span>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Van Inventory</h1>
          <p className="mt-2 text-sm text-gray-600">
            Track all vans - past and present
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/vans/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
          >
            + Add Van
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Status
            </label>
            <div className="flex space-x-2">
              <button
                onClick={() => handleFilterChange('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleFilterChange('inventory')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'inventory'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Current Inventory
              </button>
              <button
                onClick={() => handleFilterChange('sold')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'sold'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Sold
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by serial, name, VIN, or customer..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
        </div>
      </div>

      {/* Vans Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading vans...</div>
          </div>
        ) : vans.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">No vans found</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort('serial_number')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Serial # <SortIcon field="serial_number" />
                  </th>
                  <th
                    onClick={() => handleSort('name')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Name <SortIcon field="name" />
                  </th>
                  <th
                    onClick={() => handleSort('own_or_upfit')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Type <SortIcon field="own_or_upfit" />
                  </th>
                  <th
                    onClick={() => handleSort('sold')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Status <SortIcon field="sold" />
                  </th>
                  <th
                    onClick={() => handleSort('date_sold')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Date Sold <SortIcon field="date_sold" />
                  </th>
                  <th
                    onClick={() => handleSort('customer_name')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Customer <SortIcon field="customer_name" />
                  </th>
                  <th
                    onClick={() => handleSort('retail_sale_price')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Sale Price <SortIcon field="retail_sale_price" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedVans.map((van) => (
                  <tr key={van.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <Link
                        to={`/vans/${van.id}/view`}
                        className="text-gray-900 hover:text-gray-600 hover:underline"
                      >
                        {van.serial_number}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {van.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {van.own_or_upfit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          van.sold
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {van.sold ? 'Sold' : 'Inventory'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(van.date_sold)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {van.customer_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(van.retail_sale_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <Link
                        to={`/vans/${van.id}/edit`}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => deleteVan(van.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr className="border-t-2 border-gray-300">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" colSpan="6">
                    Total
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    {formatCurrency(calculateTotalSalePrice())}
                  </td>
                  <td className="px-6 py-4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 text-sm text-gray-600">
        Showing {sortedVans.length} van{sortedVans.length !== 1 ? 's' : ''}
      </div>
    </div>
  )
}
